export const CATEGORIES = [
  {
    id: "network",
    name: "Network Security",
    accent: "sky",
    tagline: "Routing, Switching, Firewalls & Enterprise Infrastructure",
    description: "From CCNA fundamentals to multi-vendor NGFW mastery — build the skills that run the internet's backbone.",
  },
  {
    id: "cloud",
    name: "Cloud Security",
    accent: "blue",
    tagline: "Azure, AWS & DevSecOps Engineering",
    description: "Design, secure and operate production cloud infrastructure the way MNC engineering teams actually do it.",
  },
  {
    id: "cyber",
    name: "Cyber Security",
    accent: "orange",
    tagline: "Offensive, Defensive & SOC Operations",
    description: "From SOC analyst to OSCP-grade pentester — hands-on attack and defense on real simulated infrastructure.",
  },
  {
    id: "ai",
    name: "AI & GenAI",
    accent: "emerald",
    tagline: "AI for Network, Cloud, Cyber & Core AI Skills",
    description: "From AI fundamentals to LLM engineering — apply AI inside your IT domain and multiply your salary.",
  },
  {
    id: "combo",
    name: "Combo Packages",
    accent: "orange",
    tagline: "Best-value bundled programs",
    description: "Bundled career packages.",
  },
];

export const COURSES = [
  // ============ NETWORK SECURITY ============
  {
    slug: "ccna",
    title: "CCNA 200-301",
    category: "network",
    level: "Beginner",
    duration: "6 Weeks",
    hours: "45+ Hours",
    tag: "Best Starting Point",
    overview: "The world's most recognised networking certification, taught 1-on-1 on live Cisco equipment. You will subnet in your sleep, configure real routers and switches, and troubleshoot with Wireshark like a TAC engineer. Every concept is first explained on a whiteboard, then built and broken in your dedicated lab pod.",
    modules: [
      "Network Fundamentals — OSI & TCP/IP models, IPv4/IPv6 addressing, subnetting mastery",
      "LAN Switching — VLANs, trunking, STP, EtherChannel, wireless LANs",
      "IP Routing — static routing, OSPFv2 single-area, first-hop redundancy",
      "IP Services — NAT, DHCP, DNS, NTP, SNMP, Syslog, QoS basics",
      "Security Fundamentals — ACLs, port security, VPN concepts, WPA2/WPA3",
      "Automation & Programmability — REST APIs, JSON, controller-based networking, Ansible intro",
    ],
    topics: [
      "OSI vs TCP/IP model — real packet walkthroughs", "IPv4 addressing & VLSM subnetting drills", "IPv6 addressing & transition mechanisms", "Ethernet standards & switching concepts",
      "VLANs, 802.1Q trunking & DTP", "STP (802.1D/RSTP) & EtherChannel (LACP/PAgP)", "Wireless LAN & WLC architecture basics", "Static & default routing deep-dive",
      "OSPFv2 single-area configuration", "First Hop Redundancy (HSRP) concepts", "NAT — static, dynamic & PAT overload", "DHCP, DNS, NTP, SNMP & Syslog services",
      "Device hardening, ACLs & port security", "VPN fundamentals & AAA (RADIUS/TACACS+)", "QoS marking & queuing basics", "Network automation — REST APIs, JSON & Python intro",
    ],
    labs: [
      "Build & subnet a complete enterprise LAN from scratch", "Configure VLANs + inter-VLAN routing on real switches", "Break & fix STP loop and broadcast-storm scenarios", "OSPF neighbour formation troubleshooting tickets",
      "NAT/PAT translation for a simulated branch office", "ACL security policy implementation & verification", "Wireshark packet-capture analysis drills", "Full CCNA mock lab exam under timed conditions",
    ],
    exam: { code: "200-301 CCNA", duration: "120 Minutes", questions: "90–110 Questions", validity: "3 Years", cost: "$300 (approx ₹25,500)" },
    salary: { india: "₹3 – 6 LPA", global: "$55K – $85K" },
    prerequisites: ["Basic computer literacy", "No prior networking experience needed", "A laptop with 8GB RAM for labs"],
    audience: ["Fresh graduates entering IT", "Helpdesk / desktop support engineers", "Anyone switching into networking"],
    jobs: ["NOC Engineer", "Network Support Engineer", "Junior Network Administrator"],
    trainer: "aviraj",
  },
  {
    slug: "ccnp-encor",
    title: "CCNP Enterprise ENCOR 350-401",
    category: "network",
    level: "Intermediate",
    duration: "8 Weeks",
    hours: "60+ Hours",
    tag: "Core Exam",
    overview: "The core of CCNP Enterprise. Deep-dive into enterprise architecture, advanced routing, SD-Access, and network automation — every topic built and broken on live pods until you can troubleshoot blindfolded. Taught exactly the way enterprise networks are designed and escalated inside Cisco TAC.",
    modules: [
      "Enterprise Architecture — campus design, SD-Access, SD-WAN overview, QoS models",
      "Virtualization — VRF-Lite, GRE/IPsec tunnels, LISP, VXLAN fundamentals",
      "Infrastructure — advanced OSPFv2/v3, EIGRP, BGP, route redistribution & filtering",
      "Layer 2 Deep Dive — MST, advanced EtherChannel, SPAN/RSPAN/ERSPAN",
      "Network Assurance — NetFlow, IP SLA, DNA Center assurance, debugging methodology",
      "Security & Automation — 802.1X, CoPP, Python scripting, NETCONF/RESTCONF",
    ],
    topics: [
      "Enterprise campus & SD-Access fabric architecture", "EIGRP named mode & optimization", "OSPFv2/v3 areas, LSA types & fast convergence", "eBGP/iBGP peering, attributes & path selection",
      "Route redistribution & loop prevention", "VRF-Lite, GRE, IPsec & LISP tunnels", "VXLAN/EVPN fabric fundamentals", "MST & advanced STP protection features",
      "EtherChannel advanced & misconfiguration guard", "HSRP/VRRP/GLBP gateway redundancy", "Wireless design, roaming & WLC deployment", "NetFlow, IP SLA & EEM scripting",
      "802.1X port authentication with ISE", "Control Plane Policing (CoPP)", "Python, NETCONF & RESTCONF automation", "QoS classification, marking & queuing models",
    ],
    labs: [
      "Multi-area OSPF with summarization & virtual links", "BGP policy control using route-maps & communities", "EVPN VXLAN fabric walkthrough on Nexus image", "SD-Access fabric demo in DNA Center",
      "IP SLA + object tracking automatic failover", "802.1X wired authentication with Cisco ISE", "Python script config push via NETCONF", "ENCOR timed mock exam with review",
    ],
    exam: { code: "350-401 ENCOR", duration: "120 Minutes", questions: "90–110 Questions", validity: "3 Years", cost: "$400 (approx ₹34,000)" },
    salary: { india: "₹6 – 14 LPA", global: "$85K – $120K" },
    prerequisites: ["CCNA-level knowledge", "6+ months hands-on networking preferred", "Willingness to lab daily"],
    audience: ["Network engineers moving to senior roles", "CCNAs upgrading to CCNP", "Engineers targeting MNC TAC roles"],
    jobs: ["Senior Network Engineer", "Network Consultant", "TAC Engineer L2"],
    trainer: "aviraj",
  },
  {
    slug: "ccnp-enarsi",
    title: "CCNP Enterprise ENARSI 300-410",
    category: "network",
    level: "Advanced",
    duration: "6 Weeks",
    hours: "45+ Hours",
    tag: "Troubleshooting Focus",
    overview: "Advanced routing and services — the concentration exam that turns network engineers into troubleshooters. Every ticket-style lab mirrors real Cisco TAC escalations: broken redistribution, flapping BGP, failing DMVPN — you fix them all before the exam.",
    modules: [
      "EIGRP & OSPF Advanced — optimisation, summarisation, stub areas, fast convergence",
      "BGP Deep Dive — path attributes, route reflectors, communities, policy control",
      "Route Manipulation — route-maps, prefix-lists, PBR, redistribution troubleshooting",
      "VPN Technologies — MPLS L3VPN, DMVPN Phase 1/2/3, IPsec troubleshooting",
      "Infrastructure Security — uRPF, CoPP, AAA, IPv6 security, device hardening",
      "Ticket-Based Troubleshooting — 40+ TAC-style broken-topology challenges",
    ],
    topics: [
      "EIGRP named mode, stubs & leak-maps", "OSPF LSA deep-dive, stub/NSSA & authentication", "OSPF fast convergence (BFD, SPF tuning)", "BGP attributes — LOCAL_PREF, MED, AS_PATH, weight",
      "BGP route reflectors & confederations", "BGP communities & outbound policy design", "Route-maps, prefix-lists & distribute-lists", "Redistribution loops & suboptimal routing fixes",
      "Policy-Based Routing (PBR)", "VRF-Lite & route leaking", "MPLS L3VPN — LDP, labels & PE-CE routing", "DMVPN Phase 1/2/3 with NHRP",
      "IPv6 routing, tunneling & transition", "AAA, uRPF, CoPP & device hardening", "Structured troubleshooting methodology", "Layer-3 ticket isolation under time pressure",
    ],
    labs: [
      "40+ TAC-style trouble tickets with grading", "MPLS L3VPN end-to-end build (P/PE/CE)", "DMVPN Phase 3 with IPsec protection", "Redistribution loop hunt & repair",
      "BGP path manipulation & failover lab", "IPv6 dual-stack migration lab", "uRPF & CoPP attack-mitigation drill", "ENARSI timed mock exam",
    ],
    exam: { code: "300-410 ENARSI", duration: "90 Minutes", questions: "55–65 Questions", validity: "3 Years", cost: "$300 (approx ₹25,500)" },
    salary: { india: "₹8 – 16 LPA", global: "$95K – $130K" },
    prerequisites: ["ENCOR-level knowledge", "Strong OSPF/BGP fundamentals", "Hands-on CLI confidence"],
    audience: ["CCNP candidates", "NOC/TAC engineers handling escalations", "Engineers preparing for CCIE later"],
    jobs: ["Senior Network Engineer", "TAC Escalation Engineer", "Network Implementation Lead"],
    trainer: "aviraj",
  },
  {
    slug: "palo-alto",
    title: "Palo Alto PCNSE & Panorama",
    category: "network",
    level: "Intermediate",
    duration: "8 Weeks",
    hours: "55+ Hours",
    tag: "Bestseller",
    overview: "Master the industry's leading next-gen firewall on dedicated Palo Alto VM pods. From App-ID to GlobalProtect at enterprise scale — taught by an engineer who deploys and troubleshoots these firewalls in production every day. Includes Panorama central management and real migration case studies.",
    modules: [
      "Next-Gen Firewall Core — App-ID, User-ID, Content-ID, zones & interfaces",
      "Security & NAT Policies — policy design, application overrides, NAT64/PT",
      "Decryption — SSL forward proxy, inbound inspection, SSH decryption, exclusions",
      "GlobalProtect — remote access VPN at scale, HIP checks, pre-logon, split tunneling",
      "High Availability & Panorama — Active/Passive & Active/Active HA, templates, device groups",
      "Threat Prevention — WildFire, DNS security, URL filtering, Zone Protection, logging",
    ],
    topics: [
      "PAN-OS architecture & session packet flow", "Zones, interface types & virtual routers", "App-ID — custom apps & application override", "User-ID — AD integration & mapping",
      "Content-ID & threat prevention profiles", "Security policy design & best-practice ordering", "NAT — source, destination, U-turn & NAT64", "SSL Forward Proxy & inbound decryption",
      "Decryption policy exclusions & certificates", "GlobalProtect portal, gateway & client", "HIP checks & pre-logon always-on VPN", "Site-to-site IPsec VPN tunnels",
      "WildFire sandboxing & DNS Security", "URL filtering & credential phishing prevention", "HA Active/Passive & Active/Active design", "Panorama templates, device groups & log collector",
    ],
    labs: [
      "Firewall bootstrap & initial hardening", "Production-grade security + NAT policy build", "SSL decryption rollout with enterprise CA", "GlobalProtect remote-access deployment",
      "HA pair failover & preempt testing", "Panorama multi-firewall template management", "Zone Protection & DoS profile tuning", "PCNSE scenario-based mock exam",
    ],
    exam: { code: "PCNSE", duration: "80 Minutes", questions: "75 Questions", validity: "2 Years", cost: "$175 (approx ₹15,000)" },
    salary: { india: "₹8 – 18 LPA", global: "$95K – $140K" },
    prerequisites: ["TCP/IP and routing basics", "Firewall concepts helpful but not mandatory", "CCNA recommended"],
    audience: ["Firewall administrators", "Network engineers entering security", "Engineers targeting PCNSE certification"],
    jobs: ["Palo Alto Security Engineer", "Network Security Analyst L2/L3", "NGFW Consultant"],
    trainer: "sonam",
  },
  {
    slug: "fortigate",
    title: "FortiGate NSE (Multi-VDOM & SD-WAN)",
    category: "network",
    level: "Intermediate",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "High Demand",
    overview: "Fortinet powers thousands of enterprises and MSSPs worldwide. Learn FortiGate from interface to SD-WAN overlay with FortiManager-driven enterprise workflows on real FortiOS labs — the exact multi-VDOM, multi-tenant designs used by managed security providers.",
    modules: [
      "FortiOS Fundamentals — interfaces, VDOMs, administrators, firmware & backups",
      "Firewall Policies — policy lookup, NAT (VIP/IP pool), traffic shaping, zones",
      "Security Profiles — IPS, antivirus, web filtering, application control, SSL inspection",
      "VPN — IPsec site-to-site, dial-up VPN, SSL VPN with FortiClient",
      "SD-WAN — members, performance SLAs, steering rules, overlay design",
      "Enterprise Management — FortiManager, FortiAnalyzer, HA clusters, logging & reporting",
    ],
    topics: [
      "FortiOS architecture & NP/CP ASIC acceleration", "Multi-VDOM segmentation & inter-VDOM links", "Administrator profiles, RBAC & MFA", "Firewall policy lookup order & flow-based inspection",
      "NAT — VIPs, IP pools & central NAT", "Traffic shaping & QoS policies", "IPS signatures & custom filters", "Antivirus flow/proxy modes & sandboxing",
      "Web & application filtering with FortiGuard", "SSL/TLS deep inspection & certificate handling", "IPsec route-based & policy-based VPN", "SSL VPN with FortiClient & MFA",
      "SD-WAN members, zones & performance SLAs", "SD-WAN steering strategies (cost, quality, priority)", "FortiManager ADOMs & policy packages", "FortiAnalyzer logging, reports & event handlers",
    ],
    labs: [
      "Multi-VDOM ISP/enterprise segmentation build", "Policy + NAT deployment scenarios", "SSL deep-inspection rollout with CA distribution", "Site-to-site IPsec between branch & HQ",
      "SD-WAN dual-link SLA steering lab", "HA (FGCP) active-passive failover test", "FortiManager zero-touch provisioning", "NSE-level diagnostic & sniffer drills",
    ],
    exam: { code: "NSE 4 (FortiOS 7.x)", duration: "60–120 Minutes", questions: "30–60 Questions", validity: "2 Years", cost: "$400 for NSE path" },
    salary: { india: "₹7 – 15 LPA", global: "$90K – $130K" },
    prerequisites: ["Basic firewall concepts", "Networking fundamentals (TCP/IP, routing)", "Lab enthusiasm"],
    audience: ["Security engineers at MSSPs", "Branch network administrators", "Engineers targeting NSE certifications"],
    jobs: ["Fortinet Security Specialist", "MSSP Security Engineer", "Network Security Administrator"],
    trainer: "sonam",
  },
  {
    slug: "f5-ltm",
    title: "F5 BIG-IP LTM (201 & 301)",
    category: "network",
    level: "Intermediate",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "Niche & High Paying",
    overview: "F5 load balancers sit in front of the world's biggest applications — banks, e-commerce, government portals. Learn BIG-IP LTM the production way: virtual servers, iRules, SSL offload and HA, on live BIG-IP VE labs, with the troubleshooting methodology used by F5 support itself.",
    modules: [
      "BIG-IP Architecture — TMOS, licensing, interfaces, VLANs, self-IPs, routes",
      "Local Traffic Management — virtual servers, pools, nodes, load balancing methods",
      "Monitors & Persistence — health monitors, source IP / cookie persistence, profiles",
      "SSL Offload & iRules — certificate management, SSL profiles, iRule events & commands",
      "SNAT & NAT — automap, SNAT pools, translation behaviour, troubleshooting flows",
      "High Availability — device groups, sync-failover, mirroring, tcpdump & bigtop analysis",
    ],
    topics: [
      "TMOS architecture, full-proxy & packet flow", "Initial setup — licensing, platform & network config", "VLANs, self-IPs, trunks & route domains", "Nodes, pools, members & priority groups",
      "Virtual server types (standard, forwarding, stateless)", "Load balancing methods — round-robin, least-conn, ratio", "Health monitors — HTTP, TCP, custom & transparent", "Persistence — source address, cookie, SSL & universal",
      "Client/Server SSL profiles & certificate chains", "SSL offloading & SSL bridging designs", "iRule events, commands & operators", "SNAT automap vs SNAT pools vs NAT",
      "OneConnect & HTTP profile tuning", "High availability — device groups & traffic groups", "Connection mirroring & failback behaviour", "tcpdump flags & qkview diagnostics on BIG-IP",
    ],
    labs: [
      "Deploy BIG-IP VE & complete base configuration", "HTTP/HTTPS VIP with layered health monitors", "iRule lab — redirects, header inserts & pool selection", "SSL offload with enterprise certificates",
      "SNAT behaviour comparison scenarios", "HA pair sync-failover & mirroring test", "tcpdump client/server-side flow analysis", "201/301-style configuration scenarios",
    ],
    exam: { code: "F5 201 & 301", duration: "90 Minutes each", questions: "80 Questions each", validity: "2 Years", cost: "$135 per exam" },
    salary: { india: "₹8 – 18 LPA", global: "$100K – $145K" },
    prerequisites: ["Networking fundamentals", "TCP handshake & TLS basics", "CLI comfort"],
    audience: ["Network engineers", "Application delivery engineers", "NOC engineers handling L4-L7 devices"],
    jobs: ["F5 Load Balancer Engineer", "Application Delivery Specialist", "Network Operations Engineer"],
    trainer: "aviraj",
  },
  {
    slug: "sd-wan",
    title: "Cisco SD-WAN (Viptela) Mastery",
    category: "network",
    level: "Advanced",
    duration: "6 Weeks",
    hours: "45+ Hours",
    tag: "Enterprise Hot Skill",
    overview: "Enterprises are ripping out MPLS for SD-WAN — and engineers who can deploy it are scarce. Learn the full Viptela stack (controllers, OMP, templates, centralized policies) by building a multi-site overlay from zero, then migrate a simulated MPLS network into it.",
    modules: [
      "SD-WAN Architecture — vManage, vSmart, vBond, WAN Edge (cEdge/vEdge), deployment models",
      "Overlay Management Protocol — OMP routes, TLOCs, tunnels, BFD sessions",
      "Configuration — CLI & template-based config, device templates, feature templates",
      "Policies — centralized control/data policies, localized policies, application-aware routing",
      "Security — IPsec encryption, DIA/cloud onramp, firewall & IPS integration, ZTP",
      "Migration & Operations — MPLS-to-SD-WAN migration, monitoring, troubleshooting tools",
    ],
    topics: [
      "SD-WAN vs traditional WAN — business case", "vManage, vSmart, vBond roles & certificate PKI", "Controller deployment (cloud & on-prem)", "WAN Edge onboarding & Zero-Touch Provisioning",
      "OMP — routes, TLOCs & path selection", "DTLS/TLS control connections & IPsec data plane", "BFD parameters, app-route & tunnel health", "Feature, device & CLI templates in vManage",
      "Centralized control policies (topology, TLOC action)", "Centralized data policies & VPN membership", "Localized policies — QoS, ACL & policers", "Application-Aware Routing (AAR) design",
      "Service-side & transport-side VPNs (segmentation)", "DIA, cloud onramp & SIG integration", "Cisco SD-WAN security stack (FW/IPS/URL-F)", "MPLS coexistence & phased migration strategies",
    ],
    labs: [
      "Bring up full controller stack (vManage/vSmart/vBond)", "Onboard 6 cEdge routers via ZTP & manual", "Build hub-spoke & full-mesh topologies", "AAR policy — route voice over MPLS, data over internet",
      "Multi-VPN segmentation (Corp/Guest/PCI)", "MPLS-to-SD-WAN migration case study", "vAnalytics & troubleshooting (ping/traceroute/app-log)", "Design review of a 200-site retail rollout",
    ],
    exam: { code: "300-415 ENSDWI", duration: "90 Minutes", questions: "55–65 Questions", validity: "3 Years", cost: "$300 (approx ₹25,500)" },
    salary: { india: "₹10 – 22 LPA", global: "$110K – $150K" },
    prerequisites: ["CCNP-level routing (BGP/OSPF)", "VPN fundamentals", "Enterprise WAN exposure helpful"],
    audience: ["WAN engineers", "Network architects", "Engineers at enterprises migrating to SD-WAN"],
    jobs: ["SD-WAN Engineer", "Network Architect", "Enterprise Network Consultant"],
    trainer: "aviraj",
  },
  {
    slug: "checkpoint",
    title: "Check Point CCSA & CCSE",
    category: "network",
    level: "Intermediate",
    duration: "6 Weeks",
    hours: "45+ Hours",
    tag: "Enterprise Firewall",
    overview: "Check Point secures banks, governments and Fortune 100 companies. Master SmartConsole, layered policy design, Identity Awareness and ClusterXL on dedicated Gaia labs — covering both CCSA and CCSE objectives in one program.",
    modules: [
      "Gaia & Architecture — Gaia OS, SMS vs gateway, SmartConsole, distributed deployment",
      "Security Policies — rulebase design, policy layers, inline layers, NAT manual/auto",
      "Identity Awareness — AD Query, Identity Collector, captive portal, access roles",
      "VPN — site-to-site communities, remote access, Mobile Access blade",
      "Threat Prevention — IPS, Anti-Bot, Anti-Virus, Threat Extraction & Emulation (sandbox)",
      "CCSE Topics — ClusterXL HA, VSX intro, SmartEvent, upgrades & advanced troubleshooting",
    ],
    topics: [
      "Check Point 3-tier architecture (SMS/Gateway/SmartConsole)", "Gaia OS installation & initial configuration", "Policy installation & database revision control", "Rulebase design — stealth, cleanup & best practices",
      "Ordered & inline policy layers", "NAT — automatic, manual & NAT rules ordering", "Identity Awareness — AD Query, Identity Collector, Captive Portal", "Access roles & identity-based rules",
      "Application Control & URL Filtering blades", "IPS blade — profiles & protections", "Anti-Bot & Anti-Virus with ThreatCloud", "Threat Extraction (CDR) & Emulation (sandboxing)",
      "Site-to-site VPN — meshed & star communities", "Remote Access & Mobile Access blade", "ClusterXL — Load Sharing & High Availability", "VSX virtual systems overview & SmartEvent reporting",
    ],
    labs: [
      "Standalone SMS + gateway deployment", "Layered policy build with inline layers", "Identity Awareness with Active Directory", "Site-to-site IPsec VPN between gateways",
      "ClusterXL HA failover & synchronization test", "Threat Emulation sandbox analysis", "SmartEvent custom reports & views", "fw monitor & kernel debug troubleshooting",
    ],
    exam: { code: "CCSA 156-215.81 & CCSE 156-315.81", duration: "90 Minutes each", questions: "90 Questions each", validity: "2 Years", cost: "$250 per exam" },
    salary: { india: "₹8 – 16 LPA", global: "$95K – $135K" },
    prerequisites: ["Networking basics (TCP/IP, NAT)", "Firewall concepts", "Virtualisation basics"],
    audience: ["Security administrators", "Firewall engineers in banking/enterprise", "CCSA/CCSE candidates"],
    jobs: ["Check Point Security Engineer", "Firewall Administrator", "Security Operations Engineer"],
    trainer: "aviraj",
  },
  {
    slug: "asa-ftd",
    title: "Cisco ASA & Firepower FTD",
    category: "network",
    level: "Intermediate",
    duration: "7 Weeks",
    hours: "50+ Hours",
    tag: "Dual Platform",
    overview: "Two Cisco firewall platforms in one program — classic ASA for the massive installed base and FTD/FMC for modern deployments, including the migration path every enterprise is walking right now. You will be the engineer who can run both.",
    modules: [
      "ASA Core — interfaces, security levels, ACLs, NAT (twice/object), MPF service policies",
      "ASA VPN — AnyConnect remote access, IKEv1/IKEv2 site-to-site, troubleshooting",
      "ASA High Availability — active/standby failover, clustering overview",
      "FTD & FMC — device registration, access control policies, prefilters, NAT",
      "IPS & Snort — Snort 3 rules, intrusion policies, file/malware policies, URL filtering",
      "Migration & Ops — ASA-to-FTD migration tool, health monitoring, packet tracer & captures",
    ],
    topics: [
      "ASA architecture & security-level traffic logic", "Interface config, redundancy & EtherChannel", "ACLs, object-groups & hit-count analysis", "NAT — object, twice, identity & NAT exemption",
      "Modular Policy Framework (MPF) & inspections", "AnyConnect — profiles, DTLS, split tunneling & posture", "IKEv1 vs IKEv2 site-to-site VPN design", "Active/Standby failover & state replication",
      "FTD architecture — Lina vs Snort engines", "FMC device registration & platform settings", "Access Control Policies — rules, zones & actions", "Prefilter policies & fast-pathing",
      "Snort 3 intrusion rules & variables", "File, malware (AMP) & URL filtering policies", "Identity policies & realm configuration", "ASA→FTD migration tool workflow & caveats",
    ],
    labs: [
      "ASA baseline — interfaces, ACLs & NAT scenarios", "AnyConnect RA VPN end-to-end deployment", "IKEv2 L2L tunnel with NAT-T", "Failover pair build & stateful switchover",
      "FMC onboarding & first access policy", "Snort rule tuning with real traffic captures", "File/malware policy with sandbox verdicts", "Live ASA→FTD migration dry-run",
    ],
    exam: { code: "300-710 SNCF (FTD)", duration: "90 Minutes", questions: "55–65 Questions", validity: "3 Years", cost: "$300 (approx ₹25,500)" },
    salary: { india: "₹8 – 17 LPA", global: "$95K – $140K" },
    prerequisites: ["CCNA-level networking", "Basic firewall concepts", "VPN fundamentals helpful"],
    audience: ["Cisco firewall administrators", "Security engineers managing ASA estates", "Engineers planning FTD migrations"],
    jobs: ["Cisco Security Engineer", "Firewall Migration Specialist", "Network Security Analyst"],
    trainer: "aviraj",
  },
  {
    slug: "ccnp-security",
    title: "CCNP Security (SCOR 350-701)",
    category: "network",
    level: "Advanced",
    duration: "10 Weeks",
    hours: "70+ Hours",
    tag: "Certification Track",
    overview: "The complete Cisco security core — ISE, TrustSec, NGFW, VPNs, email/web security and cloud security — built as one cohesive enterprise security architecture program. Ideal for engineers who want a single certification that proves end-to-end Cisco security competence.",
    modules: [
      "Security Concepts — threats, cryptography, PKI, common vulnerabilities & defenses",
      "Cisco ISE — 802.1X wired/wireless, MAB, profiling, posture, guest & BYOD",
      "TrustSec & Segmentation — SGTs, SXP, micro-segmentation design",
      "Network Security — NGFW/NGIPS architectures, VPNs (FlexVPN, GETVPN), MACsec",
      "Content & Endpoint — ESA/WSA email & web security, AMP, AnyConnect posture",
      "Secure Network Access & Cloud — AAA (TACACS+/RADIUS), Cisco Umbrella, cloud security models",
    ],
    topics: [
      "Threat landscape, CVE/CVSS & attack vectors", "Cryptography — symmetric, asymmetric, hashing & PKI", "Site-to-site & remote-access VPN architectures", "Cisco ISE deployment personas & licensing",
      "802.1X wired & wireless authentication flows", "MAB, profiling & device classification", "Posture assessment & remediation", "Guest, BYOD & sponsor workflows",
      "TrustSec — SGT assignment, SXP & SGACLs", "AAA — TACACS+ vs RADIUS device administration", "NGFW/NGIPS design & deployment modes", "FlexVPN, GETVPN & DMVPN security design",
      "MACsec & switch-port encryption", "Email security (ESA) & web security (WSA)", "Cisco Umbrella DNS-layer protection", "AMP for endpoints & AnyConnect integration",
      "Network visibility — NetFlow & Encrypted Traffic Analytics", "Cloud security models & CASB concepts",
    ],
    labs: [
      "ISE distributed deployment & AD join", "802.1X + MAB authentication policy build", "Posture compliance with AnyConnect", "TrustSec SGT segmentation enforcement",
      "FlexVPN hub-and-spoke deployment", "ESA/WSA policy walkthrough", "Umbrella protection verification", "SCOR blueprint-mapped mock exam",
    ],
    exam: { code: "350-701 SCOR", duration: "120 Minutes", questions: "90–110 Questions", validity: "3 Years", cost: "$400 (approx ₹34,000)" },
    salary: { india: "₹12 – 25 LPA", global: "$115K – $155K" },
    prerequisites: ["CCNP Enterprise or equivalent", "Firewall and VPN exposure", "Strong CLI skills"],
    audience: ["Security engineers targeting CCNP Security", "Cisco-focused security consultants", "Pre-sales & design engineers"],
    jobs: ["Security Consultant", "Senior Network Security Engineer", "Cisco TAC Security Engineer"],
    trainer: "aviraj",
  },

  // ============ CLOUD SECURITY ============
  {
    slug: "mcsa",
    title: "Windows Server (MCSA Track)",
    category: "cloud",
    level: "Beginner",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "SysAdmin Foundation",
    overview: "The bedrock of enterprise IT — Active Directory, DNS, Group Policy and Hyper-V — taught hands-on so you can run a real corporate Windows environment before your first interview. Though the MCSA brand is retired, these skills remain the #1 requirement for system administrator and Azure roles.",
    modules: [
      "Windows Server Core — installation, Server Core, roles & features, PowerShell administration",
      "Active Directory — AD DS deployment, OUs, users/groups, trusts, sites & replication",
      "DNS & DHCP — zones, forwarders, scavenging, DHCP failover, IPAM intro",
      "Group Policy — GPO design, security baselines, software deployment, preferences",
      "Virtualization & Storage — Hyper-V, failover clustering, storage spaces, file servers",
      "Hybrid Identity — Entra Connect, hybrid join, backup & disaster recovery basics",
    ],
    topics: [
      "Windows Server editions & installation options", "Server Core & PowerShell-first administration", "AD DS logical & physical design", "Domain controllers, FSMO roles & global catalog",
      "Users, groups, OUs & delegation", "Sites, subnets & replication topology", "Trust relationships & forests", "DNS zones, records & AD integration",
      "DHCP scopes, reservations & failover", "GPO inheritance, filtering & loopback", "Security baselines & password policies", "Software deployment & drive mapping via GPO",
      "Hyper-V — VMs, switches & checkpoints", "Failover clustering & quorum models", "Storage Spaces & file server (SMB, quotas, DFS)", "Entra Connect sync & hybrid join",
    ],
    labs: [
      "Build a 2-DC domain from bare VMs", "GPO security hardening rollout", "DNS + DHCP failover configuration", "Hyper-V virtual lab with checkpoints",
      "File server with quotas & shadow copies", "Hybrid identity sync to Azure tenant", "Backup & restore disaster drill", "Troubleshoot broken replication & logon issues",
    ],
    exam: null,
    salary: { india: "₹3 – 7 LPA", global: "$55K – $80K" },
    prerequisites: ["Basic computer & networking knowledge", "No prior server experience needed", "Laptop capable of running VMs"],
    audience: ["Freshers entering system administration", "Desktop support engineers upgrading", "Anyone starting the Azure path"],
    jobs: ["System Administrator", "Windows Server Engineer", "IT Infrastructure Support"],
    trainer: "piyush",
  },
  {
    slug: "azure-900",
    title: "Azure Fundamentals AZ-900",
    category: "cloud",
    level: "Beginner",
    duration: "3 Weeks",
    hours: "18+ Hours",
    tag: "Cloud Starter",
    overview: "Your first step into cloud. Understand Azure services, pricing, governance and security fundamentals — with live portal walkthroughs instead of slideware, so concepts stick because you have actually clicked through them.",
    modules: [
      "Cloud Concepts — IaaS/PaaS/SaaS, shared responsibility, CapEx vs OpEx",
      "Core Azure Services — compute, networking, storage, databases, marketplace",
      "Management & Governance — subscriptions, RBAC, policies, blueprints, resource locks",
      "Security & Identity — Entra ID basics, MFA, Defender for Cloud overview",
      "Pricing & SLA — TCO calculator, pricing calculator, service lifecycles",
      "Exam Readiness — question patterns, mock tests, live portal scenarios",
    ],
    topics: [
      "Cloud models — public, private, hybrid & multi-cloud", "IaaS vs PaaS vs SaaS with real examples", "Azure regions, availability zones & region pairs", "Resource groups & ARM architecture",
      "Compute — VMs, App Service, Functions & containers", "Networking — VNet, VPN Gateway & ExpressRoute overview", "Storage — blob, file, disk & redundancy options", "Databases — SQL, Cosmos DB & open-source options",
      "Entra ID, MFA & SSO fundamentals", "RBAC roles & scopes", "Azure Policy, blueprints, locks & tags", "Cost management, budgets & alerts",
      "SLA math & composite SLAs", "Azure Monitor & Service Health", "Defender for Cloud & Trust Center", "Marketplace & Azure Arc introduction",
    ],
    labs: [
      "Free account setup & portal tour", "Deploy first VM in 10 minutes", "Storage account + static website", "RBAC role assignment scenario",
      "Policy — deny non-compliant regions", "Budget + cost alert configuration", "TCO calculator real-world exercise", "AZ-900 full mock exam",
    ],
    exam: { code: "AZ-900", duration: "45–60 Minutes", questions: "40–60 Questions", validity: "Does not expire", cost: "$99 (approx ₹8,400)" },
    salary: { india: "₹3 – 6 LPA", global: "$55K – $80K" },
    prerequisites: ["None — absolute beginner friendly", "Free Azure account (we help you set it up)"],
    audience: ["Students & freshers", "Non-technical professionals entering cloud", "Sales/pre-sales engineers"],
    jobs: ["Cloud Support Associate", "Junior Cloud Administrator", "Cloud Sales Engineer"],
    trainer: "piyush",
  },
  {
    slug: "azure-104",
    title: "Azure Administrator AZ-104",
    category: "cloud",
    level: "Intermediate",
    duration: "7 Weeks",
    hours: "50+ Hours",
    tag: "Most Demanded",
    overview: "The certification every Azure job posting asks for. Administer identities, storage, compute and networking in a live subscription with real enterprise scenarios — landing zones, cost guardrails and production change control included.",
    modules: [
      "Identity & Governance — Entra ID, users/groups, RBAC, subscriptions, Azure Policy",
      "Storage — storage accounts, blobs/files, redundancy, lifecycle, AzCopy",
      "Compute — VMs, availability sets/zones, scale sets, App Services, AKS & ACI intro",
      "Virtual Networking — VNets, subnets, NSGs, peering, VPN gateway, load balancers",
      "Monitoring & Backup — Azure Monitor, Log Analytics, alerts, backup & site recovery",
      "Enterprise Scenarios — cost management, tagging strategy, landing zone basics",
    ],
    topics: [
      "Entra ID — users, groups, dynamic membership & SSPR", "RBAC — built-in roles, custom roles & scopes", "Subscriptions, management groups & resource hierarchy", "Azure Policy & initiative guardrails",
      "ARM templates & Bicep fundamentals", "Storage accounts — blob tiers, replication & lifecycle", "Azure Files, SMB shares & sync", "AzCopy, Storage Explorer & SAS tokens",
      "VMs — sizing, disks, extensions & availability options", "VM Scale Sets & auto-scale rules", "Azure Container Instances & AKS overview", "App Service plans, slots & deployment",
      "VNets, subnets, NSGs & ASGs", "VNet peering, VPN Gateway & Azure DNS", "Load Balancer vs Application Gateway", "Azure Monitor, Log Analytics & alert rules",
      "Azure Backup & Recovery Services Vault", "Site Recovery (ASR) replication concepts",
    ],
    labs: [
      "RBAC + policy landing-zone guardrails", "VM deployment with cloud-init extension", "Storage lifecycle & SAS security drill", "VNet peering across two regions",
      "Load-balanced web tier build", "App Service blue-green with slots", "Backup, restore & file-level recovery", "AZ-104 timed mock exam",
    ],
    exam: { code: "AZ-104", duration: "100 Minutes", questions: "40–60 Questions + Labs", validity: "1 Year (free renewal)", cost: "$165 (approx ₹14,000)" },
    salary: { india: "₹6 – 14 LPA", global: "$85K – $120K" },
    prerequisites: ["AZ-900 level knowledge", "Basic PowerShell helpful", "Windows or Linux admin basics"],
    audience: ["System administrators moving to cloud", "AZ-900 certified professionals", "IT engineers targeting Azure roles"],
    jobs: ["Azure Administrator", "Cloud Engineer", "Infrastructure Engineer (Azure)"],
    trainer: "piyush",
  },
  {
    slug: "azure-700",
    title: "Azure Network Engineer AZ-700",
    category: "cloud",
    level: "Advanced",
    duration: "6 Weeks",
    hours: "45+ Hours",
    tag: "For Network Engineers",
    overview: "Where networking meets cloud. Design and secure Azure networks — ExpressRoute, Private Link, Azure Firewall and Front Door — exactly as enterprise cloud teams do, with hub-spoke topologies built and broken in your lab subscription.",
    modules: [
      "Core Networking — VNets, subnets, peering, service endpoints, NAT gateway",
      "Hybrid Connectivity — VPN gateway, ExpressRoute circuits, BGP over ExpressRoute",
      "Load Balancing — Azure Load Balancer, Application Gateway, Front Door, Traffic Manager",
      "Network Security — Azure Firewall & Firewall Manager, NSGs/ASGs, DDoS protection, WAF",
      "Private Access — Private Link, private endpoints, DNS private zones design",
      "Routing & Monitoring — UDRs, NVA deployments, Network Watcher, connection monitor",
    ],
    topics: [
      "VNet design, CIDR planning & subnet strategy", "VNet peering vs VPN gateway connectivity", "Service endpoints & subnet delegation", "NAT gateway & outbound connectivity design",
      "Hybrid DNS — private zones & resolver endpoints", "S2S & P2S VPN gateway deep-dive", "ExpressRoute circuits, peers & QoS", "BGP routing over ExpressRoute & VPN",
      "Azure Virtual WAN & hub design", "Azure Route Server & NVA integration", "Load Balancer SKUs, rules & probes", "Application Gateway — L7 routing & WAF policies",
      "Front Door & Traffic Manager global routing", "Azure Firewall — rules, DNAT & threat intel", "Firewall Manager & secured virtual hubs", "Private Link & private endpoint DNS patterns",
      "UDRs & forced tunneling", "Network Watcher — topology, packet capture & NSG flow logs",
    ],
    labs: [
      "Hub-spoke topology with central firewall", "ExpressRoute + VPN failover simulation", "Application Gateway with WAF policy", "Private Link to storage — no public exposure",
      "Route Server BGP with NVA", "DDoS & NSG flow log analysis", "Forced tunneling through firewall", "AZ-700 scenario mock exam",
    ],
    exam: { code: "AZ-700", duration: "100 Minutes", questions: "40–60 Questions + Labs", validity: "1 Year (free renewal)", cost: "$165 (approx ₹14,000)" },
    salary: { india: "₹10 – 20 LPA", global: "$105K – $145K" },
    prerequisites: ["Strong networking fundamentals (CCNA+)", "AZ-104 level Azure knowledge", "BGP basics"],
    audience: ["Network engineers entering cloud", "Azure admins specialising in networking", "Cloud architects"],
    jobs: ["Azure Network Engineer", "Cloud Network Architect", "Hybrid Connectivity Specialist"],
    trainer: "piyush",
  },
  {
    slug: "azure-500",
    title: "Azure Security Engineer AZ-500",
    category: "cloud",
    level: "Advanced",
    duration: "7 Weeks",
    hours: "55+ Hours",
    tag: "Security Flagship",
    overview: "Azure's premier security certification. Lock down identity with Conditional Access and PIM, build detections in Microsoft Sentinel, and defend workloads with Defender for Cloud — all in live labs that simulate real attack paths.",
    modules: [
      "Identity Security — Entra ID protection, Conditional Access, MFA, PIM, access reviews",
      "Platform Protection — NSGs, Azure Firewall, DDoS, Private Link, perimeter design",
      "Data & Application Security — Key Vault, disk/storage encryption, SQL security, app secrets",
      "Security Operations — Microsoft Sentinel, KQL detection rules, incident management",
      "Defender for Cloud — CSPM, workload protection, vulnerability management, regulatory compliance",
      "Governance & Compliance — Azure Policy for security, blueprints, landing zone guardrails",
    ],
    topics: [
      "Entra ID Protection — risky users & sign-in policies", "Conditional Access policies — design & report-only rollout", "MFA, SSPR & passwordless (FIDO2)", "Privileged Identity Management (PIM) & JIT access",
      "Access reviews & entitlement management", "Hybrid identity security — PTA, PHS & hardening", "NSGs, ASGs & micro-segmentation", "Azure Firewall & DDoS Network Protection",
      "Private Link & zero-public-IP architectures", "Key Vault — keys, secrets, certificates & RBAC", "Disk (ADE/SSE), storage & TDE encryption", "SQL auditing, data masking & Advanced Threat Protection",
      "Microsoft Sentinel — connectors, analytics & incidents", "KQL — detection rules & threat hunting queries", "Defender for Cloud — secure score & recommendations", "Just-in-time VM access & adaptive hardening",
      "Container & AKS security posture", "Regulatory compliance dashboards & policy-as-code",
    ],
    labs: [
      "Conditional Access rollout without lockouts", "PIM — elevate, approve & audit admin roles", "Key Vault secret retrieval from a VM (managed identity)", "Sentinel analytics rule catching brute-force login",
      "KQL threat-hunting workbook build", "Defender for Cloud secure-score remediation sprint", "JIT VM access enablement", "AZ-500 attack-path mock exam",
    ],
    exam: { code: "AZ-500", duration: "100 Minutes", questions: "40–60 Questions + Labs", validity: "1 Year (free renewal)", cost: "$165 (approx ₹14,000)" },
    salary: { india: "₹10 – 22 LPA", global: "$110K – $150K" },
    prerequisites: ["AZ-104 level knowledge", "Security fundamentals", "Active Directory basics"],
    audience: ["Azure administrators moving to security", "SOC analysts adopting Sentinel", "Security engineers in Microsoft shops"],
    jobs: ["Azure Security Engineer", "Cloud Security Analyst", "Sentinel Engineer"],
    trainer: "piyush",
  },
  {
    slug: "azure-400",
    title: "Azure DevOps Engineer AZ-400",
    category: "cloud",
    level: "Advanced",
    duration: "7 Weeks",
    hours: "55+ Hours",
    tag: "DevSecOps Track",
    overview: "Ship infrastructure like a product. Build secure CI/CD pipelines, IaC with Bicep/Terraform, and DevSecOps guardrails used inside Microsoft's own engineering teams — with pipelines you design, break and secure yourself.",
    modules: [
      "Source Control & Planning — Git workflows, branching strategies, Azure Boards/GitHub Projects",
      "CI Pipelines — Azure Pipelines & GitHub Actions, build agents, artifact management",
      "Infrastructure as Code — Bicep, ARM templates, Terraform on Azure, desired state",
      "Release & Deployment — deployment slots, blue-green/canary, approvals & gates",
      "DevSecOps — secret management, SonarQube, container scanning, pipeline security",
      "Monitoring & Feedback — Application Insights, dashboards, SRE practices",
    ],
    topics: [
      "Git internals — branching, merging & rebasing strategies", "Trunk-based vs GitFlow in enterprises", "Azure Pipelines — YAML schema & stages", "GitHub Actions — workflows, runners & environments",
      "Microsoft-hosted vs self-hosted agents & pools", "Artifact feeds & package management", "Bicep modules & deployment stacks", "ARM templates & what-if deployments",
      "Terraform on Azure — state, backends & pipelines", "Deployment slots & slot swapping", "Blue-green, canary & ring deployments", "Pre/post-deployment approvals & quality gates",
      "Key Vault secrets inside pipelines", "SonarQube/SonarCloud quality gates", "Container & dependency scanning (Trivy, Defender)", "Application Insights & release annotations",
      "Dashboards, alerts & SRE error budgets", "Secure DevOps — service connections & least privilege",
    ],
    labs: [
      "Full CI/CD pipeline — commit to production", "Bicep infrastructure deploy via pipeline", "Canary release with automatic rollback gate", "SonarQube quality gate blocking a bad build",
      "Key Vault-backed secret injection", "Container scan gate in GitHub Actions", "App Insights availability & failure alerts", "AZ-400 case-study mock exam",
    ],
    exam: { code: "AZ-400", duration: "100 Minutes", questions: "40–60 Questions + Labs", validity: "1 Year (free renewal)", cost: "$165 (approx ₹14,000)" },
    salary: { india: "₹12 – 24 LPA", global: "$115K – $155K" },
    prerequisites: ["AZ-104 or AWS equivalent", "Git fundamentals", "Basic scripting (Bash/PowerShell)"],
    audience: ["Admins moving to DevOps", "Developers owning deployments", "Engineers targeting platform/SRE roles"],
    jobs: ["DevOps Engineer", "Platform Engineer", "SRE (Azure)"],
    trainer: "piyush",
  },
  {
    slug: "aws-practitioner",
    title: "AWS Cloud Practitioner CLF-C02",
    category: "cloud",
    level: "Beginner",
    duration: "3 Weeks",
    hours: "18+ Hours",
    tag: "AWS Starter",
    overview: "The perfect launchpad into AWS. Core services, billing, security and architecture basics — taught with live console labs and real-world cost scenarios so you understand the bill as well as the technology.",
    modules: [
      "Cloud Concepts — value of cloud, shared responsibility model, economics",
      "Core Services — EC2, S3, RDS, Lambda, VPC overview, CloudFront",
      "Security & Compliance — IAM basics, artifact, compliance programs, GuardDuty intro",
      "Billing & Pricing — pricing models, cost explorer, budgets, support plans",
      "Cloud Architecture — Well-Architected pillars, global infrastructure, HA concepts",
      "Exam Readiness — scenario questions, timed mocks, console walkthroughs",
    ],
    topics: [
      "Cloud value proposition & economics (CapEx→OpEx)", "AWS global infrastructure — regions, AZs & edge", "Shared responsibility model boundaries", "IAM users, groups, roles & policies intro",
      "EC2 instance families & pricing models", "S3 — buckets, classes & static hosting", "EBS, EFS & instance store", "RDS vs DynamoDB — relational vs NoSQL",
      "Lambda & serverless concepts", "VPC, subnets & security groups overview", "CloudFront CDN & Route53 DNS basics", "ELB & Auto Scaling concepts",
      "Well-Architected six pillars", "Cost Explorer, Budgets & billing alerts", "Support plans comparison", "Compliance programs & AWS Artifact",
    ],
    labs: [
      "Free-tier account setup with billing guardrails", "Launch & connect to first EC2 instance", "S3 static website behind CloudFront", "IAM role assignment exercise",
      "Monthly cost estimation for a startup stack", "CLF-C02 timed mock exam", "Well-Architected review mini-workshop", "Console scavenger hunt — 20 services",
    ],
    exam: { code: "CLF-C02", duration: "90 Minutes", questions: "65 Questions", validity: "3 Years", cost: "$100 (approx ₹8,500)" },
    salary: { india: "₹3 – 6 LPA", global: "$55K – $80K" },
    prerequisites: ["None — beginner friendly", "Free-tier AWS account (we set it up together)"],
    audience: ["Students & career switchers", "Managers & non-technical roles", "Anyone starting the AWS track"],
    jobs: ["Cloud Support Associate", "Junior Cloud Engineer", "AWS Sales/Pre-sales Associate"],
    trainer: "sonam",
  },
  {
    slug: "aws-saa",
    title: "AWS Solutions Architect Associate SAA-C03",
    category: "cloud",
    level: "Intermediate",
    duration: "7 Weeks",
    hours: "55+ Hours",
    tag: "Most Popular",
    overview: "The certification that opens cloud careers. Architect highly available, fault-tolerant, cost-optimised systems on AWS — every design built live in your own account and reviewed against the Well-Architected Framework.",
    modules: [
      "Compute — EC2 deep dive, Auto Scaling, Lambda, ECS/EKS, Elastic Beanstalk",
      "Storage & Databases — S3 & classes, EBS/EFS, RDS, Aurora, DynamoDB, ElastiCache",
      "Networking — VPC design, subnets, route tables, NAT, ALB/NLB, Route53, CloudFront",
      "Security — IAM policies & roles, KMS, Secrets Manager, security groups vs NACLs",
      "Resilient Architectures — multi-AZ, multi-region, decoupling with SQS/SNS, backups & DR",
      "Cost & Performance — Well-Architected Framework, cost optimisation, caching strategies",
    ],
    topics: [
      "EC2 — families, placement groups & purchase options", "Auto Scaling policies & lifecycle hooks", "Lambda triggers, limits & cost model", "ECS vs EKS vs Fargate decision framework",
      "S3 storage classes, lifecycle & replication", "EBS vs EFS vs FSx — workload mapping", "RDS Multi-AZ vs Aurora vs read replicas", "DynamoDB keys, indexes & DAX caching",
      "ElastiCache Redis/Memcached patterns", "VPC — subnets, route tables, NAT gateways & IGW", "ALB vs NLB vs GLB selection", "Route53 routing policies & health checks",
      "CloudFront caching, origins & invalidation", "IAM policy evaluation logic & roles", "KMS, Secrets Manager & Parameter Store", "SQS, SNS & EventBridge decoupling patterns",
      "Multi-AZ vs multi-region & DR (RPO/RTO)", "Well-Architected pillars applied to case studies",
    ],
    labs: [
      "3-tier HA architecture — ALB → ASG → RDS Multi-AZ", "S3 + CloudFront secure static site", "Serverless API with Lambda + DynamoDB", "RDS failover & point-in-time restore",
      "SQS-based decoupled order-processing system", "VPC with public/private subnets + NAT", "Cost-optimization audit of a messy account", "SAA-C03 timed mock exam with review",
    ],
    exam: { code: "SAA-C03", duration: "130 Minutes", questions: "65 Questions", validity: "3 Years", cost: "$150 (approx ₹12,750)" },
    salary: { india: "₹8 – 18 LPA", global: "$100K – $140K" },
    prerequisites: ["Cloud Practitioner or equivalent", "Basic networking & Linux", "Willingness to build daily"],
    audience: ["Engineers targeting cloud architect roles", "SysAdmins moving to AWS", "Developers owning infrastructure"],
    jobs: ["AWS Solutions Architect", "Cloud Engineer (AWS)", "DevOps Engineer"],
    trainer: "sonam",
  },
  {
    slug: "aws-networking",
    title: "AWS Advanced Networking Specialty",
    category: "cloud",
    level: "Advanced",
    duration: "6 Weeks",
    hours: "45+ Hours",
    tag: "Specialty Cert",
    overview: "The hardest and most respected AWS specialty certification. Master Transit Gateway, Direct Connect, hybrid DNS and multi-account network architectures at enterprise scale — for engineers who want to be the person called when connectivity is down.",
    modules: [
      "VPC Mastery — advanced VPC design, CIDR planning, NAT architectures, endpoint services",
      "Hybrid Connectivity — Direct Connect (dedicated/hosted), Site-to-Site VPN, BGP routing",
      "Transit Gateway — multi-account designs, route tables, sharing with RAM, inspection VPCs",
      "DNS & Edge — Route53 routing policies, Resolver endpoints, hybrid DNS, CloudFront, Global Accelerator",
      "Network Security — Network Firewall, PrivateLink, SG/NACL strategies, WAF at edge",
      "Operations — flow logs, Reachability Analyzer, Network Manager, troubleshooting labs",
    ],
    topics: [
      "VPC design at scale — CIDR strategy & IPAM", "NAT gateway vs NAT instance vs egress-only", "VPC endpoints — gateway vs interface (PrivateLink)", "Bring Your Own IP (BYOIP)",
      "Direct Connect dedicated vs hosted connections", "DX gateways, VIFs & link aggregation (LAG)", "Site-to-Site VPN — tunnels, CGW & acceleration", "BGP attributes in AWS hybrid routing",
      "Transit Gateway route tables & segmentation", "TGW peering & cross-region design", "RAM sharing & multi-account topologies", "Centralized inspection VPC architecture",
      "Route53 Resolver inbound/outbound endpoints", "Hybrid DNS between on-prem & AWS", "CloudFront, Global Accelerator & edge networking", "Network Firewall — Suricata rules & TLS inspection",
      "SG/NACL layering & traffic analysis", "Flow logs, Traffic Mirroring & Reachability Analyzer",
    ],
    labs: [
      "Multi-VPC Transit Gateway with inspection VPC", "DX + VPN automatic failover design", "Hybrid DNS resolution lab", "PrivateLink service provider/consumer build",
      "Network Firewall rule deployment & logging", "Troubleshoot asymmetric routing with flow logs", "BGP local-pref community failover", "ANS-C01 scenario mock exam",
    ],
    exam: { code: "ANS-C01", duration: "170 Minutes", questions: "65 Questions", validity: "3 Years", cost: "$300 (approx ₹25,500)" },
    salary: { india: "₹14 – 28 LPA", global: "$125K – $170K" },
    prerequisites: ["SAA-level AWS knowledge", "Strong networking background (CCNP helpful)", "BGP & DNS fundamentals"],
    audience: ["Network engineers on AWS", "Cloud architects designing hybrid networks", "Engineers targeting networking specialty cert"],
    jobs: ["AWS Network Engineer", "Cloud Network Architect", "Hybrid Cloud Specialist"],
    trainer: "sonam",
  },
  {
    slug: "aws-security",
    title: "AWS Security Specialty SCS-C02",
    category: "cloud",
    level: "Advanced",
    duration: "8 Weeks",
    hours: "55+ Hours",
    tag: "Security Flagship",
    overview: "Defend AWS the way AWS itself does. IAM internals, KMS encryption design, GuardDuty threat detection and automated incident response — practised on attack-simulation labs where you detect and contain live (simulated) breaches.",
    modules: [
      "IAM Deep Dive — policies, permission boundaries, SCPs, roles anywhere, federation",
      "Data Protection — KMS & envelope encryption, CloudHSM, Secrets Manager, Macie",
      "Infrastructure Security — VPC security patterns, WAF, Shield, Network Firewall",
      "Threat Detection — GuardDuty, Security Hub, Inspector, Detective, CloudTrail forensics",
      "Incident Response — automated containment with Lambda, isolation playbooks, IR drills",
      "Logging & Compliance — centralised logging, Config rules, audit manager, compliance frameworks",
    ],
    topics: [
      "IAM policy evaluation logic — explicit deny & boundaries", "SCPs in multi-account Organizations", "Federation, Identity Center & roles anywhere", "KMS key policies, grants & envelope encryption",
      "CloudHSM use-cases & key ceremony", "Secrets Manager rotation patterns", "Macie — PII discovery in S3", "VPC security — private subnets, SG referencing & prefixes",
      "WAF rules, rate-limiting & Shield Advanced", "Network Firewall & DNS firewall", "GuardDuty finding types & suppression rules", "Security Hub standards & custom insights",
      "Inspector — EC2/ECR/Lambda vulnerability scans", "Detective — behaviour graphs for forensics", "CloudTrail Lake & organisation trails", "Config rules & conformance packs",
      "Automated IR — quarantine instance & revoke keys with Lambda", "Compliance — PCI, HIPAA, ISO mapping on AWS",
    ],
    labs: [
      "SCP guardrails in a 3-account Organization", "KMS envelope encryption on S3 + RDS", "GuardDuty crypto-mining finding — full IR drill", "Lambda auto-containment playbook",
      "Security Hub CIS benchmark remediation sprint", "Macie PII discovery report", "WAF rate-limit + geo-block deployment", "SCS-C02 scenario mock exam",
    ],
    exam: { code: "SCS-C02", duration: "170 Minutes", questions: "65 Questions", validity: "3 Years", cost: "$300 (approx ₹25,500)" },
    salary: { india: "₹14 – 30 LPA", global: "$125K – $170K" },
    prerequisites: ["SAA-level knowledge", "IAM & VPC fundamentals", "Security mindset"],
    audience: ["Cloud engineers moving to security", "Security engineers adopting AWS", "Engineers targeting the security specialty cert"],
    jobs: ["AWS Cloud Security Architect", "DevSecOps Engineer", "Cloud Security Consultant"],
    trainer: "sonam",
  },

  // ============ CYBER SECURITY ============
  {
    slug: "linux-kali",
    title: "Linux & Kali for Security Professionals",
    category: "cyber",
    level: "Beginner",
    duration: "4 Weeks",
    hours: "30+ Hours",
    tag: "Essential Foundation",
    overview: "Every security career runs on Linux. Master the command line, Bash scripting and the Kali toolkit until the terminal feels like home — the non-negotiable base for CEH, OSCP and every SOC role.",
    modules: [
      "Linux Fundamentals — filesystem, users & permissions, processes, package management",
      "Command Line Mastery — grep, awk, sed, find, pipes, text processing for log analysis",
      "Bash Scripting — variables, loops, conditionals, automation scripts for recon",
      "Networking in Linux — netstat, ss, tcpdump, netcat, iptables, SSH hardening",
      "Kali Toolset — Nmap, Nikto, Hydra, Metasploit basics, Burp Suite setup",
      "Services & Hardening — Apache/SSH/FTP configuration, logging, CIS baseline intro",
    ],
    topics: [
      "Linux distros & security-focused variants", "Filesystem hierarchy & navigation mastery", "Users, groups, permissions & SUID/SGID", "Package management with apt & dpkg",
      "Processes, daemons & systemd services", "grep, awk, sed & cut for log surgery", "find, locate & file-forensics basics", "Bash scripting — variables, loops & functions",
      "Cron jobs & task automation", "ip, ss, netstat & network debugging", "tcpdump & Wireshark CLI capture", "netcat — the swiss-army knife",
      "iptables & UFW firewall rules", "SSH keys, hardening & tunneling", "Kali installation & tool categories", "Nmap, Nikto, Hydra & Metasploit first steps",
    ],
    labs: [
      "Build & harden a Linux server from scratch", "Bash recon-automation script project", "tcpdump credential-capture analysis (own lab)", "Nmap full network sweep & service fingerprinting",
      "Hydra brute-force against lab SSH", "Log-analysis challenge — find the intruder", "iptables lockdown without self-lockout", "Kali tool-kit familiarisation gauntlet",
    ],
    exam: null,
    salary: { india: "₹3 – 6 LPA", global: "$50K – $75K" },
    prerequisites: ["None — beginner friendly", "A laptop that can run VirtualBox/VMware"],
    audience: ["Absolute beginners in cyber security", "Students preparing for CEH/OSCP", "IT support staff entering security"],
    jobs: ["Junior SOC Analyst (foundation)", "IT Security Support", "Pentest Intern"],
    trainer: "anu",
  },
  {
    slug: "ceh-v13",
    title: "CEH v13 — Certified Ethical Hacker",
    category: "cyber",
    level: "Intermediate",
    duration: "8 Weeks",
    hours: "60+ Hours",
    tag: "Global Recognition",
    overview: "The latest CEH with AI-driven attack labs. Learn the complete hacker methodology — from footprinting to cloud exploitation — in a dedicated attack-and-defend range where every technique is executed by you, not watched on a slide.",
    modules: [
      "Recon & Scanning — footprinting, OSINT, Nmap mastery, vulnerability scanning",
      "System Hacking — enumeration, password attacks, privilege escalation, covering tracks",
      "Malware & Sniffing — trojans, ransomware analysis, Wireshark deep dive, MITM attacks",
      "Web & Wireless Hacking — OWASP Top 10, SQLi, XSS, WiFi attacks (WPA3, evil twin)",
      "Modern Attack Surfaces — cloud, IoT, OT attacks, mobile hacking, AI-powered attack tools",
      "Defense & Reporting — IDS/IPS evasion, honeypots, cryptography, professional reporting",
    ],
    topics: [
      "Ethical hacking legality, scope & methodology", "Footprinting — OSINT, Shodan & Google dorks", "Nmap — stealth scans, NSE scripts & evasion", "Enumeration — SMB, SNMP, LDAP & NFS",
      "Vulnerability analysis with Nessus & OpenVAS", "System hacking — password cracking & privesc", "Malware analysis — trojans, ransomware & droppers", "Sniffing & MITM — ARP spoofing, Wireshark",
      "Social engineering & phishing frameworks", "DoS/DDoS techniques & detection", "Session hijacking & token theft", "Web server attacks & misconfiguration exploitation",
      "OWASP Top 10 — SQLi, XSS, SSRF & file upload", "Wireless attacks — WPA2/WPA3, evil twin & deauth", "Mobile platform hacking (Android/iOS)", "IoT & OT attack surfaces",
      "Cloud attacks — S3 misconfig, IAM abuse & container escape", "Cryptography — attacks on weak implementations", "IDS/IPS & firewall evasion", "AI-powered hacking tools (CEH v13 additions)",
    ],
    labs: [
      "Full kill-chain on a lab target — recon to root", "Phishing campaign simulation (GoPhish)", "WPA2 handshake capture & crack", "SQLi to web-shell walkthrough",
      "Malware detonation in isolated sandbox", "MITM + credential interception drill", "Cloud S3 bucket exploitation scenario", "CEH v13 practice exam (125 questions)",
    ],
    exam: { code: "312-50 (CEH v13)", duration: "4 Hours", questions: "125 Questions", validity: "3 Years", cost: "$950 knowledge exam" },
    salary: { india: "₹6 – 15 LPA", global: "$85K – $125K" },
    prerequisites: ["Linux/Kali basics (or take our Linux course first)", "Networking fundamentals", "TCP/IP understanding"],
    audience: ["Aspiring ethical hackers", "SOC analysts leveling up", "Engineers needing CEH for compliance roles"],
    jobs: ["Ethical Hacker", "VAPT Analyst", "Security Consultant"],
    trainer: "sonam",
  },
  {
    slug: "comptia-security-plus",
    title: "CompTIA Security+ SY0-701",
    category: "cyber",
    level: "Beginner",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "Best First Cert",
    overview: "The certification most security job descriptions list first. Security+ 701 taught with live demos — not just theory — so you pass the exam and actually understand the concepts behind every domain.",
    modules: [
      "General Security Concepts — CIA triad, zero trust, cryptography basics, change management",
      "Threats & Vulnerabilities — attack surfaces, malware types, social engineering, indicators",
      "Security Architecture — secure network design, cloud & virtualization security, resilience",
      "Security Operations — SIEM, hardening, incident response, forensics basics",
      "Program Management — governance, risk, compliance, third-party risk, audits",
      "Exam & PBQ Mastery — performance-based question drills, 500+ practice questions",
    ],
    topics: [
      "Security control categories & types", "CIA triad, non-repudiation & AAA", "Zero Trust architecture principles", "Cryptography — symmetric, asymmetric, hashing & PKI",
      "Threat actors — nation-state, insiders & hacktivists", "Malware types & indicators of compromise", "Social engineering & physical attacks", "Vulnerability management & patching",
      "Secure network architecture & segmentation", "Cloud & virtualization security models", "Identity & authentication — MFA, SSO & federation", "Resilience — redundancy, backups & DR",
      "SIEM, SOAR & security monitoring", "Endpoint & OS hardening baselines", "Incident response lifecycle & playbooks", "Digital forensics & evidence handling",
      "Governance — policies, standards & frameworks (NIST, ISO)", "Risk management & third-party assessments", "Compliance, audits & attestations", "Change management & secure processes",
    ],
    labs: [
      "Risk register & assessment exercise", "SIEM log-analysis investigation", "Phishing email triage drill", "Firewall rule-base audit",
      "Password-cracking resistance demo", "PKI certificate deployment walkthrough", "Incident-response tabletop simulation", "PBQ-style timed scenario bank",
    ],
    exam: { code: "SY0-701", duration: "90 Minutes", questions: "Up to 90 (incl. PBQs)", validity: "3 Years", cost: "$404 (approx ₹34,000)" },
    salary: { india: "₹4 – 8 LPA", global: "$65K – $95K" },
    prerequisites: ["Basic IT knowledge", "Network+ helpful but not required", "No experience needed"],
    audience: ["Freshers entering cyber security", "IT professionals adding security credentials", "Compliance & GRC aspirants"],
    jobs: ["SOC Analyst L1", "Security Analyst", "IT Auditor (Junior)"],
    trainer: "anu",
  },
  {
    slug: "comptia-pentest-plus",
    title: "CompTIA PenTest+ PT0-003",
    category: "cyber",
    level: "Intermediate",
    duration: "7 Weeks",
    hours: "50+ Hours",
    tag: "Hands-on Pentest",
    overview: "The full engagement lifecycle — scoping, recon, exploitation, post-exploitation and professional reporting — rehearsed on vulnerable lab networks until the methodology becomes muscle memory. Includes a complete mock engagement with a graded client report.",
    modules: [
      "Engagement Management — scoping, rules of engagement, legal & compliance, communication",
      "Recon & Enumeration — passive/active recon, OSINT, service & vulnerability enumeration",
      "Attacks & Exploits — network, web app, wireless, cloud and social engineering attacks",
      "Post-Exploitation — lateral movement, persistence, privilege escalation, data exfil simulation",
      "Tools & Scripting — Metasploit, Nmap NSE, Burp Suite, Bash/Python automation",
      "Reporting & Remediation — executive summaries, technical findings, CVSS scoring, retesting",
    ],
    topics: [
      "Engagement types — black/white/grey box", "Scoping documents, ROE & legal authorisation", "Passive recon — OSINT frameworks & Shodan", "Active recon — Nmap & service fingerprinting",
      "Vulnerability scanning & false-positive analysis", "Network service exploitation (SMB, SSH, FTP)", "Password attacks — spraying, cracking & stuffing", "Web app exploitation with Burp Suite",
      "SQLi, XSS, IDOR & file-upload exploitation", "Wireless attacks — WPA cracking & rogue APs", "Cloud & container attack paths", "Social engineering & physical pretexting",
      "Privilege escalation — Linux & Windows checklists", "Lateral movement & pivoting techniques", "Persistence & C2 concepts", "Covering tracks & detection awareness",
      "Bash/Python scripting for pentesters", "Report writing, CVSS 4.0 scoring & remediation advice",
    ],
    labs: [
      "Complete mock engagement — scope to signed report", "AD lab — enumeration to domain admin", "Web app exploitation chain on OWASP target", "Wireless capture & crack on lab AP",
      "Privilege-escalation gauntlet (10 boxes)", "Professional pentest report — graded deliverable", "Retest workflow & finding validation", "PT0-003 scenario & PBQ mock exam",
    ],
    exam: { code: "PT0-003", duration: "165 Minutes", questions: "Up to 90 (incl. PBQs)", validity: "3 Years", cost: "$404 (approx ₹34,000)" },
    salary: { india: "₹7 – 16 LPA", global: "$90K – $130K" },
    prerequisites: ["Security+ level knowledge", "Linux & networking basics", "Hands-on lab experience helpful"],
    audience: ["Security analysts moving to offensive roles", "VAPT engineers formalising skills", "Engineers targeting PenTest+ cert"],
    jobs: ["Penetration Tester", "VAPT Engineer", "Red Team Operator (Junior)"],
    trainer: "anu",
  },
  {
    slug: "cissp",
    title: "CISSP — Certified Information Systems Security Professional",
    category: "cyber",
    level: "Advanced",
    duration: "10 Weeks",
    hours: "60+ Hours",
    tag: "Leadership Cert",
    overview: "The gold standard for security leadership. All 8 domains taught by a CISSP-certified principal engineer, with the 'think like a manager' mindset training that actually passes this adaptive exam — plus risk, BCP and governance case studies from real enterprises.",
    modules: [
      "Security & Risk Management — governance, risk frameworks, BCP, legal & ethics",
      "Asset Security & Architecture — data classification, security models, cryptography, secure design",
      "Communication & Network Security — secure network architecture, protocols, attacks & controls",
      "Identity & Access Management — AAA models, federation, identity lifecycle, access control",
      "Assessment, Testing & Operations — security testing, logging, incident management, DR",
      "Software Development Security — SDLC security, DevSecOps, application vulnerabilities",
    ],
    topics: [
      "Security governance, policies & organisational roles", "Risk management frameworks (NIST RMF, ISO 27005)", "Business continuity & disaster recovery planning", "Legal, regulations & professional ethics",
      "Data classification, ownership & lifecycle", "Privacy — GDPR & data protection principles", "Security models — Bell-LaPadula, Biba & Clark-Wilson", "Cryptography — algorithms, PKI & key management",
      "Secure design principles & cloud models", "Physical & facility security", "OSI-layer security controls & protocols", "Network attacks & countermeasure design",
      "IAM lifecycle — provisioning to deprovisioning", "Federated identity — SAML, OAuth & OIDC", "Access control models — DAC, MAC, RBAC & ABAC", "Security assessment & testing strategies",
      "Logging, monitoring & SIEM operations", "Incident management & evidence handling", "Secure SDLC & DevSecOps practices", "Application vulnerabilities & maturity models",
    ],
    labs: [
      "Enterprise risk-assessment case study", "BCP/DR tabletop exercise", "Cryptography implementation review", "Incident-response tabletop with injects",
      "IAM architecture design workshop", "250-question CAT adaptive simulator", "Domain-wise weakness diagnosis & remediation plan", "Mindset coaching — 'manager, not admin' drills",
    ],
    exam: { code: "CISSP CAT", duration: "3 Hours", questions: "100–150 Adaptive", validity: "3 Years (CPE maintained)", cost: "$749 (approx ₹64,000)" },
    salary: { india: "₹18 – 40 LPA", global: "$120K – $165K" },
    prerequisites: ["5 years security experience (or degree waiver)", "Security+ / hands-on background", "Management mindset readiness"],
    audience: ["Senior security engineers", "Security managers & consultants", "Engineers targeting CISO-track roles"],
    jobs: ["Security Manager", "Security Architect", "GRC Lead / CISO Track"],
    trainer: "sonam",
  },
  {
    slug: "oscp",
    title: "OSCP Preparation — Offensive Security",
    category: "cyber",
    level: "Advanced",
    duration: "10 Weeks",
    hours: "80+ Hours",
    tag: "Hardcore Hands-on",
    overview: "Try Harder — with a guide. Real exploitation on a simulated corporate Active Directory environment: enumeration, privesc, pivoting and buffer overflows, plus exam-strategy coaching from an OSCP holder. 30+ practice machines and two full 24-hour mock exams.",
    modules: [
      "Enumeration Mastery — deep service enumeration, web enumeration, AD enumeration",
      "Web Exploitation — SQLi, file inclusion, upload bypasses, authentication attacks",
      "Active Directory Attacks — Kerberoasting, AS-REP roasting, delegation, BloodHound, lateral movement",
      "Privilege Escalation — Linux & Windows privesc methodology, kernel exploits, misconfigurations",
      "Buffer Overflows & Pivoting — stack BOF step-by-step, tunneling, port forwarding, SOCKS proxies",
      "Exam Strategy — reporting, time management, 24-hour lab simulations, 30+ practice machines",
    ],
    topics: [
      "Methodology, note-taking & evidence discipline", "Passive & active enumeration depth", "Web enumeration — vhosts, directories & parameters", "SQLi — union, blind & out-of-band",
      "LFI/RFI, file-upload & authentication bypasses", "Password attacks & hashcat rule-crafting", "Common service exploitation — SMB, FTP, RDP, WinRM", "Linux privesc — SUID, sudo, cron & capabilities",
      "Windows privesc — services, tokens & registry", "AD enumeration — PowerView & BloodHound", "Kerberoasting, AS-REP roasting & delegation abuse", "Lateral movement — PsExec, WMI & pass-the-hash",
      "Windows stack buffer overflow — fuzz to shell", "Pivoting — SSH tunnels, Chisel & Ligolo", "Antivirus evasion fundamentals", "Metasploit — when & how it's allowed",
      "Exam report structure & screenshot evidence", "24-hour time & mental-management strategy",
    ],
    labs: [
      "30+ PG/HTB-style practice machines with walkthrough review", "Full AD forest compromise — child to root DC", "Buffer overflow exploitation end-to-end", "3-tier network pivoting challenge",
      "Two complete 24-hour mock exams with scoring", "Exam-grade report writing (graded twice)", "Linux privesc gauntlet — 15 boxes", "Windows privesc gauntlet — 15 boxes",
    ],
    exam: { code: "OSCP (Pen-200)", duration: "23h 45m + 24h report", questions: "5 Machines + AD Set", validity: "Lifetime", cost: "$1,749 (90-day lab)" },
    salary: { india: "₹12 – 30 LPA", global: "$110K – $160K" },
    prerequisites: ["Strong Linux & networking", "CEH/PenTest+ level experience", "Scripting basics (Bash/Python)"],
    audience: ["Serious pentest aspirants", "Security professionals targeting OSCP", "Red team enthusiasts"],
    jobs: ["Penetration Tester (OSCP)", "Red Team Specialist", "Security Researcher"],
    trainer: "anu",
  },

  // ============ AI & GENAI ============
  {
    slug: "ai-fundamentals",
    title: "AI Fundamentals & Prompt Engineering",
    category: "ai",
    level: "Beginner",
    duration: "4 Weeks",
    hours: "25+ Hours",
    tag: "Start AI Here",
    overview: "The perfect entry into AI for any IT professional. Understand how modern AI actually works, master prompt engineering for daily engineering work, and learn to use AI tools 10x faster than your peers — no coding background required.",
    modules: [
      "AI Demystified — what AI/ML/GenAI really are, how LLMs think, capabilities & limits",
      "Prompt Engineering Core — instructions, context, roles, few-shot patterns, chain-of-thought",
      "AI for Daily IT Work — documentation, config generation, troubleshooting, email & reports",
      "AI Tooling — ChatGPT, Claude, Gemini, Copilot, Perplexity — when to use what",
      "Data & Privacy Basics — what never to paste into AI, enterprise-safe usage",
      "Build Your AI Workflow — personal prompt library, automation habits, evaluation checklist",
    ],
    topics: [
      "AI vs ML vs Deep Learning vs GenAI — clear mental models", "How LLMs generate answers (tokens, context windows)", "Hallucinations — why they happen & how to catch them", "Prompt anatomy — role, task, context, format, constraints",
      "Few-shot & chain-of-thought prompting", "System prompts & instruction hierarchy", "Prompting for network configs & firewall rules", "Prompting for cloud architectures & Terraform",
      "Prompting for security analysis & log triage", "AI-assisted documentation & SOP writing", "Image & diagram generation for presentations", "AI search vs Google — research workflows",
      "Data privacy & safe enterprise AI usage", "Evaluating AI output quality systematically", "Building a personal prompt library", "AI productivity stack setup",
    ],
    labs: [
      "Build a prompt pack for your current job role", "Generate & verify a network config with AI", "AI-assisted troubleshooting of a broken lab", "Research report built with AI in 30 minutes",
      "Hallucination hunting — catch 10 AI mistakes", "Create an AI usage policy for a company", "Prompt engineering mini-tournament", "Final project — your personal AI workflow",
    ],
    exam: null,
    salary: { india: "₹5 – 9 LPA", global: "$70K – $95K" },
    prerequisites: ["None — open to everyone", "Any IT background is a plus", "Just curiosity and a laptop"],
    audience: ["Any IT professional wanting AI skills", "Students & freshers", "Managers who want to lead AI adoption"],
    jobs: ["AI-Ready IT Engineer", "Junior Prompt Engineer", "AI Operations Associate"],
    trainer: "piyush",
  },
  {
    slug: "ml-python",
    title: "Machine Learning Foundations with Python",
    category: "ai",
    level: "Beginner",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "Core Skill",
    overview: "Learn Python and machine learning the engineer's way — practical, visual, zero heavy math. Train your first models, understand what's happening under the hood, and build the foundation every AI/ML role expects.",
    modules: [
      "Python Essentials — variables, loops, functions, files, virtual environments",
      "Data Handling — NumPy & Pandas for real datasets, cleaning, exploration",
      "ML Concepts — supervised vs unsupervised, features, training vs testing, overfitting",
      "Core Algorithms — regression, decision trees, random forests, clustering, classification",
      "Model Evaluation — accuracy, precision, recall, confusion matrix, cross-validation",
      "Mini Projects — prediction models on real IT & security datasets",
    ],
    topics: [
      "Python setup — VS Code, Jupyter & venv", "Data types, loops & functions refresher", "NumPy arrays & vectorized operations", "Pandas DataFrames — filter, group, merge",
      "Data cleaning & handling missing values", "Visualization with Matplotlib & Seaborn", "Train/test split & why it matters", "Linear & logistic regression intuition",
      "Decision trees & random forests", "K-means clustering & anomaly detection", "Classification metrics that matter", "Overfitting, underfitting & regularization",
      "Feature engineering basics", "Cross-validation & model selection", "Intro to neural networks (conceptual)", "Where ML fits in IT, cloud & security",
    ],
    labs: [
      "Python & Jupyter environment setup", "Clean & explore a real network-traffic dataset", "Train a classifier to detect suspicious logins", "Clustering lab — group servers by behaviour",
      "Regression lab — predict bandwidth usage", "Confusion matrix deep-dive on security data", "Build & evaluate 3 models head-to-head", "Capstone — ML model on your own dataset",
    ],
    exam: null,
    salary: { india: "₹6 – 12 LPA", global: "$80K – $110K" },
    prerequisites: ["Basic computer skills", "No programming experience needed", "Laptop with 8GB RAM"],
    audience: ["Engineers wanting ML foundations", "Analysts moving toward data/AI roles", "Students targeting AI careers"],
    jobs: ["Junior ML Engineer", "Data Analyst", "AI Support Engineer"],
    trainer: "piyush",
  },
  {
    slug: "ai-network-engineers",
    title: "AI for Network Engineers (AIOps)",
    category: "ai",
    level: "Intermediate",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "Career Multiplier",
    overview: "AI is eating network operations — engineers who combine CCNP-level skills with AI get paid like architects. Learn AIOps, AI-assisted troubleshooting, anomaly detection on network data, and LLM-powered automation for configs and tickets.",
    modules: [
      "AIOps Landscape — what AIOps is, tools (Splunk ITSI, Cisco AI Analytics, Moogsoft), use cases",
      "AI-Assisted Troubleshooting — LLM workflows for logs, traces & TAC-style tickets",
      "Anomaly Detection — ML on NetFlow/SNMP/syslog data, baselining, alert noise reduction",
      "LLM-Powered Automation — generating & validating configs, Ansible with AI, safe guardrails",
      "Predictive Operations — capacity & failure prediction, proactive alerting design",
      "Capstone — build an AI-assisted NOC workflow end-to-end",
    ],
    topics: [
      "AIOps vs traditional monitoring", "Event correlation & noise reduction with ML", "Anomaly detection on interface & flow data", "Time-series baselining for network metrics",
      "LLM prompts for syslog & trap analysis", "AI-assisted root-cause analysis workflow", "Config generation with validation guardrails", "AI + Ansible — safe automation patterns",
      "ChatOps — LLM bots for NOC teams", "Ticket summarization & escalation drafting", "Predictive capacity planning models", "Vendor AI features — Cisco, Juniper Mist, Arista",
      "RAG over your own network documentation", "Evaluating AI answers before pushing config", "Security & change-control for AI automation", "Building an AI-assisted runbook library",
    ],
    labs: [
      "Anomaly detection on live NetFlow dataset", "LLM syslog triage — 50 alerts to 5 incidents", "AI-generated OSPF config + validation pipeline", "ChatOps bot answering network queries",
      "Predictive alert — interface failure forecast", "AI-drafted TAC escalation from raw logs", "RAG chatbot over network runbooks", "Capstone — AI-assisted NOC simulation",
    ],
    exam: null,
    salary: { india: "₹12 – 22 LPA", global: "$110K – $150K" },
    prerequisites: ["CCNA/CCNP-level networking", "Basic Python helpful (we cover what's needed)", "NOC/TAC exposure is a plus"],
    audience: ["Network engineers & NOC/TAC staff", "Network automation engineers", "Anyone targeting AIOps roles"],
    jobs: ["AIOps Engineer", "Network Automation Engineer (AI)", "Senior NOC/TAC Engineer"],
    trainer: "aviraj",
  },
  {
    slug: "ai-cloud-engineers",
    title: "AI for Cloud Engineers (Azure AI + AWS Bedrock)",
    category: "ai",
    level: "Intermediate",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "Hot in Market",
    overview: "Every cloud job description now lists AI services. Master Azure OpenAI, Azure AI Foundry, AWS Bedrock and SageMaker hands-on — deploy models, build RAG apps on cloud data, and secure AI workloads like a professional.",
    modules: [
      "Cloud AI Landscape — Azure AI services vs AWS AI services, when to use what",
      "Azure OpenAI & AI Foundry — deployments, prompt flow, content safety, private networking",
      "AWS Bedrock — models, agents, knowledge bases, guardrails",
      "RAG on Cloud — vector databases, embeddings, document search over enterprise data",
      "Cost & Performance — token economics, model selection, caching, quotas",
      "Securing AI Workloads — IAM for AI, private endpoints, data governance, responsible AI",
    ],
    topics: [
      "Azure OpenAI deployment & model catalog", "Prompt Flow — build & evaluate AI flows", "Azure AI Search + vector indexes", "AI Foundry projects & observability",
      "AWS Bedrock model access & inference", "Bedrock Agents & action groups", "Knowledge Bases — RAG without servers", "Guardrails — PII filters & topic blocking",
      "Embeddings & vector DB choices (Pinecone, pgvector)", "Chunking strategies for enterprise docs", "Token pricing & cost control patterns", "Private endpoints & VNet integration for AI",
      "IAM & key management for AI services", "Content safety & responsible AI dashboards", "SageMaker vs Bedrock — decision framework", "Multi-model routing & fallback design",
    ],
    labs: [
      "Deploy GPT model in Azure OpenAI + call from app", "Build RAG chatbot over company PDFs (Azure)", "Bedrock Knowledge Base over S3 documents", "Bedrock Agent that checks AWS resources",
      "Add guardrails & test jailbreak resistance", "Cost dashboard for AI token usage", "Private-networked AI deployment (no public IP)", "Capstone — enterprise document assistant",
    ],
    exam: { code: "AI-102 / AWS AIF-C01 aligned", duration: "100 Minutes", questions: "40–60 Questions", validity: "1 Year (free renewal)", cost: "$165 (approx ₹14,000)" },
    salary: { india: "₹12 – 22 LPA", global: "$115K – $155K" },
    prerequisites: ["AZ-104 or AWS SAA level knowledge", "Basic scripting (Python/JS)", "AI Fundamentals course or equivalent"],
    audience: ["Cloud engineers & architects", "DevOps engineers adding AI skills", "Developers deploying AI on cloud"],
    jobs: ["Cloud AI Engineer", "AI Solutions Engineer", "GenAI Platform Engineer"],
    trainer: "piyush",
  },
  {
    slug: "ai-cyber-security",
    title: "AI for Cyber Security (AI-Powered SOC & LLM Security)",
    category: "ai",
    level: "Intermediate",
    duration: "6 Weeks",
    hours: "40+ Hours",
    tag: "Future of SOC",
    overview: "AI is transforming both attack and defense. Learn to run an AI-powered SOC — ML anomaly detection, LLM-assisted triage and threat hunting — and to defend the new frontier: prompt injection, LLM vulnerabilities and AI model attacks.",
    modules: [
      "AI in the SOC — where ML beats rules, UEBA concepts, AI SIEM features (Sentinel, Splunk AI)",
      "LLM-Assisted Analysis — alert triage, log summarization, incident report drafting with AI",
      "ML for Threat Detection — anomaly detection on auth logs, phishing classification, malware scoring",
      "AI-Powered Attacks — deepfakes, AI phishing, automated recon, polymorphic malware",
      "LLM Security (OWASP LLM Top 10) — prompt injection, data leakage, jailbreaks, RAG poisoning",
      "Securing AI in the Enterprise — AI usage policies, model access control, monitoring AI traffic",
    ],
    topics: [
      "UEBA & behavioural analytics concepts", "ML anomaly detection on authentication logs", "Phishing email classification with ML", "LLM-assisted alert triage workflows",
      "Threat-hunting queries co-written with AI", "AI-generated incident reports & timelines", "MITRE ATT&CK mapping with LLM assistance", "AI-powered phishing & deepfake demo",
      "Automated recon — how attackers use AI", "OWASP LLM Top 10 deep-dive", "Prompt injection — direct & indirect", "Jailbreaking techniques & defenses",
      "RAG poisoning & vector DB attacks", "Model data leakage & membership inference", "AI gateway & monitoring patterns", "Enterprise AI usage policy design",
    ],
    labs: [
      "ML anomaly detector on VPN auth logs", "LLM triage — 100 alerts summarized & prioritized", "Phishing classifier trained & tested", "Prompt-injection attack lab (own sandbox)",
      "Jailbreak a test LLM, then defend it", "Deepfake & AI-phishing awareness demo", "AI-assisted incident report from raw logs", "Capstone — AI-powered mini-SOC workflow",
    ],
    exam: null,
    salary: { india: "₹12 – 24 LPA", global: "$115K – $160K" },
    prerequisites: ["Security+ / SOC basics", "Basic networking & Linux", "AI Fundamentals course or equivalent"],
    audience: ["SOC analysts & threat hunters", "Security engineers adopting AI", "Pentesters exploring LLM security"],
    jobs: ["AI-SOC Analyst", "LLM Security Engineer", "Threat Detection Engineer"],
    trainer: "sonam",
  },
  {
    slug: "genai-llm",
    title: "Generative AI & LLM Engineering (RAG + Agents)",
    category: "ai",
    level: "Advanced",
    duration: "8 Weeks",
    hours: "55+ Hours",
    tag: "Highest Paying",
    overview: "The flagship AI program — build production-grade GenAI systems. RAG pipelines, AI agents, fine-tuning, evaluation and deployment, with the engineering discipline (guardrails, observability, cost control) that separates demos from products.",
    modules: [
      "LLM Engineering Core — APIs, tokens, temperature, structured output, function calling",
      "RAG Pipelines — embeddings, chunking, vector stores, hybrid search, reranking, evaluation",
      "AI Agents — tool use, planning, ReAct patterns, multi-agent workflows, LangGraph basics",
      "Fine-Tuning & Customization — when to fine-tune, LoRA/QLoRA concepts, dataset preparation",
      "Production Engineering — guardrails, observability, evals, latency & cost optimization",
      "Capstone — deploy a full enterprise AI assistant with your own data",
    ],
    topics: [
      "LLM API mastery — OpenAI, Anthropic, open models", "Structured output & JSON mode", "Function calling & tool use patterns", "Embeddings deep-dive & model selection",
      "Chunking strategies that actually work", "Vector stores — pgvector, Pinecone, Qdrant", "Hybrid search & reranking (BM25 + vectors)", "RAG evaluation — faithfulness & relevance metrics",
      "Agent architectures — ReAct, planning, reflection", "LangChain/LangGraph workflow building", "Multi-agent patterns & orchestration", "Memory — short-term, long-term & entity memory",
      "Fine-tuning vs RAG vs prompting — decision framework", "LoRA/QLoRA fine-tuning concepts & demo", "Guardrails — input/output filtering & PII", "LLM observability — traces, evals & monitoring",
      "Latency, caching & cost optimization", "Deploying LLM apps — Docker, APIs & scaling",
    ],
    labs: [
      "Build a function-calling assistant from scratch", "RAG pipeline over 500+ document corpus", "Agent that browses, computes & reports", "Multi-agent research team simulation",
      "Fine-tune a small model on domain data", "Eval harness — measure RAG quality objectively", "Guardrails + PII redaction pipeline", "Capstone — production AI assistant with monitoring",
    ],
    exam: null,
    salary: { india: "₹25 – 50 LPA", global: "$150K – $220K" },
    prerequisites: ["Python proficiency", "ML Foundations or equivalent", "API & cloud basics"],
    audience: ["Engineers targeting AI architect roles", "Senior developers & platform engineers", "Tech leads building AI products"],
    jobs: ["AI Solutions Architect", "LLM Engineer", "Head of AI / AI Lead"],
    trainer: "piyush",
  },

  // ============ COMBO ============
  {
    slug: "interview-mastery-combo",
    title: "Complete Interview Mastery Combo — Network + Cloud + Cyber",
    category: "combo",
    level: "Intermediate",
    duration: "4 Weeks",
    hours: "25+ Hours",
    tag: "Most Selling",
    overview: "Our most-selling combo: interview preparation for ALL tracks in one package. Technical mock interviews with real MNC rubrics across networking, cloud and cyber security, plus resume, LinkedIn, HR rounds and salary negotiation — everything between you and the offer letter.",
    modules: [
      "Resume & LinkedIn Engineering — ATS-proof resume, recruiter-magnet LinkedIn, project storytelling",
      "Network Security Mock Interviews — CCNA/CCNP/Palo Alto/FortiGate grill sessions with TAC rubrics",
      "Cloud Security Mock Interviews — Azure/AWS scenario rounds, architecture whiteboarding",
      "Cyber Security Mock Interviews — SOC scenarios, pentest methodology rounds, case questions",
      "HR & Managerial Rounds — behavioural answers, salary negotiation, notice-period handling",
      "Offer Sprint — interview scheduling strategy, feedback loops, final evaluation & improvement plan",
    ],
    topics: [
      "ATS-proof resume structure & keywords", "LinkedIn profile that recruiters find", "Explaining projects with STAR method", "OSI/TCP rapid-fire drill",
      "Firewall scenario questions (Palo Alto/FortiGate)", "Routing & troubleshooting whiteboard rounds", "Cloud architecture design questions", "IAM & cloud security scenario grill",
      "SOC alert triage case rounds", "Pentest methodology walkthrough questions", "'Tell me about yourself' — perfected", "Salary negotiation scripts",
      "Handling 'I don't know' gracefully", "Questions to ask the interviewer", "Body language & online interview setup", "Post-interview follow-up strategy",
    ],
    labs: [
      "3 full technical mock interviews (recorded & reviewed)", "Resume teardown & rebuild workshop", "LinkedIn makeover live session", "Whiteboard architecture round",
      "HR round simulation with real recruiter rubric", "Salary negotiation role-play", "Stress interview drill — rapid fire", "Final evaluation with hiring-manager feedback",
    ],
    exam: null,
    salary: { india: "₹6 – 35 LPA (role-dependent)", global: "$70K – $160K" },
    prerequisites: ["Completed or pursuing any course track", "Basics of your chosen domain", "Ready to be grilled (politely)"],
    audience: ["Job seekers in network/cloud/cyber", "Freshers facing first IT interviews", "Experienced engineers switching companies"],
    jobs: ["Network/Cloud/Cyber roles — per your track", "Faster offer conversion", "Higher salary negotiation outcome"],
    trainer: "aviraj",
  },
];

export const PRICING = { Beginner: "₹12,000", Intermediate: "₹20,000", Advanced: "₹35,000" };

export const INCLUDED = [
  "Live 1-on-1 training sessions",
  "Live class recordings",
  "24x7 dedicated lab access",
  "Notes PDF & lab workbooks",
  "1-on-1 mentor support",
  "Mock interviews & resume help",
];

export const getCourse = (slug) => COURSES.find((c) => c.slug === slug);
export const getCoursesByCategory = (cat) => COURSES.filter((c) => c.category === cat);
export const getCategory = (id) => CATEGORIES.find((c) => c.id === id);
